import { Button, Checkbox, message, Modal, Table } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ApiError, fetchWithResponse } from "../../../utils/EasyFetch";
import { loading, notLoading } from "../../../features/loading/loadingSlice";
import { errorNameToMessage } from "../../../utils/ErrorMessages";
import CreateView from "./CreateView";
import EditWowView from "./EditWowView";
import EditLolView from "./EditLolView";
import {
  selectAccessAuthorization,
  selectRefreshAuthorization,
  selectUser,
  selectUserRoles,
  updateAccessToken,
} from "../../../features/login/loginSlice";
import { Cookies } from "react-cookie";
import { LoginResponse } from "../../../features/login/types";
import {
  basicLolEntityRequest,
  basicWowEntityRequest,
  LolEntityCreation,
  SimpleView,
  simpleViewToRow,
  SimpleRow,
  View,
  ViewDeleted,
  ViewModified,
  viewToEditView,
  WowEntityCreation,
  Metadata,
} from "../../../types/views";

type Entity = WowEntityCreation | LolEntityCreation;

const ViewsLayout = () => {
  const [views, setViews] = useState<SimpleView[]>([]);
  const [view, setView] = useState({
    name: "new-view",
    game: "WOW",
    entities: [] as Entity[],
    published: true,
    featured: false,
  });
  const [currentView, setCurrentView] = useState<View | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalForEdit, setIsModalForEdit] = useState(false);
  const [metadata, setMetadata] = useState<{ totalCount: number }>({
    totalCount: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const accessToken = useAppSelector(selectAccessAuthorization);
  const refreshToken = useAppSelector(selectRefreshAuthorization);
  const user = useAppSelector(selectUser);
  const roles = useAppSelector(selectUserRoles);
  const dispatch = useAppDispatch();

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    dispatch(loading());
    const cookies = new Cookies();
    fetchWithResponse<{ metadata: Metadata; records: SimpleView[] }>(
      "GET",
      `/views?page=${currentPage}&size=${pageSize}`,
      undefined,
      "Bearer " + accessToken
    )
      .then((v) => {
        setViews(v.records);
        setMetadata(v.metadata);
        dispatch(notLoading());
      })
      .catch((err: ApiError) => {
        if (err.statusCode === 401) {
          fetchWithResponse<LoginResponse>(
            "POST",
            "/auth/refresh",
            undefined,
            "Bearer " + refreshToken
          )
            .then((a: LoginResponse) => {
              cookies.set("accessToken", a.accessToken, {
                path: "/",
                sameSite: true,
              });
              dispatch(updateAccessToken(a.accessToken));
              dispatch(notLoading());
            })
            .catch((err: any) => {
              dispatch(notLoading());
              messageApi.open({
                type: "error",
                content: errorNameToMessage(err.name),
              });
            });
        } else {
          dispatch(notLoading());

          messageApi.open({
            type: "error",
            content: errorNameToMessage(err.name),
          });
        }
      });
  }, [
    accessToken,
    currentPage,
    dispatch,
    messageApi,
    refreshToken,
    user,
    roles,
    pageSize,
  ]);

  const showCreateModal = () => {
    setView({
      name: "new-view",
      game: "WOW",
      entities: [basicWowEntityRequest],
      published: true,
      featured: false,
    });
    setIsModalOpen(true);
    setIsModalForEdit(false);
  };

  const showEditModal = (text: string, record: any) => {
    dispatch(loading());
    fetchWithResponse<View>(
      "GET",
      "/views/" + record.key,
      undefined,
      "Bearer " + accessToken
    )
      .then((v: View) => {
        dispatch(notLoading());
        setCurrentView(v);
        setView({
          ...view,
          game: v.game,
        });
        setIsModalOpen(true);
        setIsModalForEdit(true);
      })
      .catch((err: any) => {
        dispatch(notLoading());
        messageApi.open({
          type: "error",
          content: errorNameToMessage(err.name),
        });
      });
  };

  const handleCreateOk = () => {
    dispatch(loading());
    fetchWithResponse<ViewModified>(
      "POST",
      "/views",
      { ...view, published: true, featured: false },
      "Bearer " + accessToken
    )
      .then((response) => {
        dispatch(notLoading());
        const vm = response;
        const simpleView = {
          id: vm.id,
          owner: user!,
          name: view.name,
          entities: vm.entities,
          published: true,
          featured: false,
          game: view.game,
        };
        setViews([...views, simpleView]);
        setIsModalOpen(false);
      })
      .catch((err: any) => {
        dispatch(notLoading());
        messageApi.open({
          type: "error",
          content: errorNameToMessage(err.name),
        });
      });
  };

  const handleEditOk = () => {
    dispatch(loading());
    fetchWithResponse<ViewModified>(
      "PUT",
      "/views/" + currentView?.id,
      viewToEditView(currentView!),
      "Bearer " + accessToken
    )
      .then((vm: ViewModified) => {
        dispatch(notLoading());
        const updatedViews = views.map((v) =>
          v.id === vm.id ? { ...v, ...vm } : v
        );
        setViews(updatedViews);
        setIsModalOpen(false);
      })
      .catch((err: any) => {
        dispatch(notLoading());
        messageApi.open({
          type: "error",
          content: errorNameToMessage(err.name),
        });
      });
  };

  const handleDelete = (text: String, record: any) => {
    Modal.confirm({
      title: "Delete view",
      content: "This can't be undone",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      maskClosable: true,
      onOk: () => {
        dispatch(loading());
        fetchWithResponse<ViewDeleted>(
          "DELETE",
          "/views/" + record.key,
          undefined,
          "Bearer " + accessToken
        )
          .then((vd) => {
            dispatch(notLoading());
            setViews(views.filter((v) => v.id !== vd.viewId));
          })
          .catch((err: any) => {
            dispatch(notLoading());
            messageApi.open({
              type: "error",
              content: errorNameToMessage(err.name),
            });
          });
      },
    });
  };

  const handlePublish = (text: String, record: any) => {
    dispatch(loading());
    const v = views.find((view) => view.id === record.key)!;
    fetchWithResponse<ViewModified>(
      "PATCH",
      "/views/" + record.key,
      { published: !v.published, game: v.game },
      "Bearer " + accessToken
    )
      .then((vm) => {
        dispatch(notLoading());
        const updatedViews = views.map((v) =>
          v.id === vm.id ? { ...v, ...vm } : v
        );
        setViews(updatedViews);
      })
      .catch((err: any) => {
        dispatch(notLoading());
        messageApi.open({
          type: "error",
          content: errorNameToMessage(err.name),
        });
      });
  };

  const handleFeature = (text: String, record: any) => {
    dispatch(loading());
    const v = views.find((view) => view.id === record.key)!;
    fetchWithResponse<ViewModified>(
      "PATCH",
      "/views/" + record.key,
      { featured: !v.featured, game: v.game },
      "Bearer " + accessToken
    )
      .then((vm) => {
        dispatch(notLoading());
        const updatedViews = views.map((v) =>
          v.id === vm.id ? { ...v, ...vm } : v
        );
        setViews(updatedViews);
      })
      .catch((err: any) => {
        dispatch(notLoading());
        messageApi.open({
          type: "error",
          content: errorNameToMessage(err.name),
        });
      });
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  const handleEntityChange = (index: number, entity: Entity) => {
    if (isModalForEdit && currentView) {
      const updatedEntities = currentView.entities.map((ent, i) =>
        i === index ? entity : ent
      );
      setCurrentView((prevView) =>
        prevView ? { ...prevView, entities: updatedEntities } : null
      );
    } else {
      const updatedEntities = view.entities.map((ent, i) =>
        i === index ? entity : ent
      );
      setView((prevView) => ({
        ...prevView,
        entities: updatedEntities,
      }));
    }
  };

  const getEntityRequest = (game: string) => {
    switch (game) {
      case "WOW":
        return basicWowEntityRequest;
      case "LOL":
        return basicLolEntityRequest;
      case "WOW_HC":
        return basicWowEntityRequest;
      default:
        return basicWowEntityRequest;
    }
  };

  const handleGameChange = (game: string) => {
    const newEntityRequest = getEntityRequest(game);

    if (isModalForEdit && currentView) {
      setCurrentView((prevView) =>
        prevView
          ? {
              ...prevView,
              game,
              entites: [newEntityRequest],
            }
          : null
      );
    } else {
      setView({
        ...view,
        game,
        entities: [newEntityRequest],
      });
    }
  };

  const handleViewNameChange = (name: string) => {
    if (isModalForEdit && currentView) {
      setCurrentView((prevView) => (prevView ? { ...prevView, name } : null));
    } else {
      setView({ ...view, name });
    }
  };

  const handleAddEntity = () => {
    const newEntity = getEntityRequest(view.game);

    if (isModalForEdit && currentView) {
      setCurrentView((prevView) =>
        prevView
          ? { ...prevView, entities: [...prevView.entities, newEntity] }
          : null
      );
    } else {
      setView({ ...view, entities: [...view.entities, newEntity] });
    }
  };

  const handleDeleteEntity = (index: number) => {
    if (isModalForEdit && currentView) {
      const updatedEntities = currentView.entities.filter(
        (_, i) => i !== index
      );
      setCurrentView((prevView) =>
        prevView ? { ...prevView, entities: updatedEntities } : null
      );
    } else {
      const updatedEntities = view.entities.filter((_, i) => i !== index);
      setView({ ...view, entities: updatedEntities });
    }
  };

  const columns = [
    {
      title: "View",
      dataIndex: "viewName",
      key: "viewName",
    },
    {
      title: "Url",
      dataIndex: "viewUrl",
      key: "viewUrl",
      render: (text: string, record: any) => (
        <a href={record.viewUrl} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ),
    },
    ...(roles && roles.includes("ADMIN")
      ? [
          {
            title: "View Owner",
            dataIndex: "viewOwner",
            key: "viewOwner",
          },
        ]
      : []),
    {
      title: "Edit",
      key: "edit",
      render: (text: string, record: any) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => showEditModal(text, record)}
        />
      ),
    },
    {
      title: "Delete",
      key: "delete",
      render: (text: string, record: any) => (
        <Button
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(text, record)}
        />
      ),
    },
    {
      title: "Published",
      key: "published",
      render: (text: string, record: any) => (
        <Checkbox
          checked={record.published}
          onClick={() => handlePublish(text, record)}
        ></Checkbox>
      ),
    },
    ...(roles && roles.includes("ADMIN")
      ? [
          {
            title: "Featured",
            key: "featured",
            render: (text: string, record: any) => {
              return (
                <Checkbox
                  checked={record.featured}
                  onClick={() => handleFeature(text, record)}
                ></Checkbox>
              );
            },
          },
        ]
      : []),
  ];

  const getEditViewComponent = (game: string, currentView: View) => {
    switch (game) {
      case "WOW":
        return (
          <EditWowView
            view={currentView}
            onEntityChange={handleEntityChange}
            onViewNameChange={handleViewNameChange}
            onAddEntity={handleAddEntity}
            onDeleteEntity={handleDeleteEntity}
          />
        );
      case "LOL":
        return (
          <EditLolView
            view={currentView}
            onEntityChange={handleEntityChange}
            onViewNameChange={handleViewNameChange}
            onAddEntity={handleAddEntity}
            onDeleteEntity={handleDeleteEntity}
          />
        );
      case "WOW_HC":
        return (
          <EditWowView
            view={currentView}
            onEntityChange={handleEntityChange}
            onViewNameChange={handleViewNameChange}
            onAddEntity={handleAddEntity}
            onDeleteEntity={handleDeleteEntity}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {contextHolder}
      <Table
        dataSource={views
          .map(simpleViewToRow)
          .filter((row): row is SimpleRow => row !== undefined)}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: metadata?.totalCount ?? 0,
          onChange: (page) => setCurrentPage(page),
        }}
        bordered={true}
        showHeader={true}
        style={{ margin: "1%" }}
      />
      <Modal
        title={isModalForEdit ? "Edit View" : "Create View"}
        open={isModalOpen}
        onOk={handleCreateOk}
        onCancel={handleModalCancel}
        footer={[
          <Button
            key="back"
            onClick={isModalForEdit ? handleModalCancel : handleModalCancel}
          >
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            onClick={isModalForEdit ? handleEditOk : handleCreateOk}
          >
            {isModalForEdit ? "Edit View" : "Create View"}
          </Button>,
        ]}
      >
        {isModalForEdit ? (
          getEditViewComponent(currentView?.game ?? "", currentView!)
        ) : (
          <CreateView
            view={view}
            onEntityChange={handleEntityChange}
            onGameChange={handleGameChange}
            onViewNameChange={handleViewNameChange}
            onAddEntity={handleAddEntity}
            onDeleteEntity={handleDeleteEntity}
          />
        )}
      </Modal>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          showCreateModal();
        }}
      >
        Create New View
      </Button>
    </div>
  );
};

export default ViewsLayout;
